import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DataTable from '../components/Tables/DataTable';
import CustomerForm from '../components/Forms/CustomerForm';
import CustomerProjectsModal from '../components/Modals/CustomerProjectsModal';
import { Plus, Folder } from 'lucide-react';
import { calculateCustomerFinances } from '../utils/calculations';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

const Customers = () => {
    const { currency } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    // const [totalPages, setTotalPages] = useState(0);
    const [total, setTotal] = useState(0);
    const [showForm, setShowForm] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [selectedCustomerForProjects, setSelectedCustomerForProjects] = useState<any>(null);

    const fetchCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/customers?page=${page}&limit=10`);
            setCustomers(res.data.customers);
            // setTotalPages(res.data.pagination.pages);
            setTotal(res.data.pagination.total);
        } catch (err) {
            console.error('Error fetching customers', err);
        } finally {
            setLoading(false);
        }
    }, [page]);

    useEffect(() => {
        fetchCustomers();
    }, [fetchCustomers]);

    const handleCreate = () => {
        setEditingCustomer(null);
        setShowForm(true);
    };

    const handleEdit = (customer: any) => {
        setEditingCustomer(customer);
        setShowForm(true);
    };

    const handleDelete = async (customer: any) => {
        if (window.confirm('Are you sure you want to delete this customer?')) {
            try {
                await api.delete(`/customers/${customer.id}`);
                fetchCustomers();
            } catch (err) {
                console.error('Error deleting customer', err);
            }
        }
    };

    const columns = [
        { header: 'Name', accessor: 'displayName' },
        { header: 'Company Name', accessor: 'companyName' },
        { header: 'Email', accessor: 'email' },
        { header: 'Work Phone', accessor: 'workPhone' },

        {
            header: 'Receivables',
            accessor: (item: any) => {
                const { outstanding } = calculateCustomerFinances(item);
                return formatCurrency(outstanding, currency);
            }
        },
    ];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Customers</h1>
                <button
                    onClick={handleCreate}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
                >
                    <Plus size={18} />
                    New
                </button>
            </div>

            <DataTable
                data={customers}
                columns={columns}
                total={total}
                page={page}
                limit={10}
                onPageChange={(p) => setPage(p)}
                onEdit={handleEdit}
                onDelete={handleDelete}
                customActions={(item) => (
                    <button
                        onClick={() => setSelectedCustomerForProjects(item)}
                        className="text-gray-500 hover:text-blue-600 p-1"
                        title="View Projects"
                    >
                        <Folder size={16} />
                    </button>
                )}
                loading={loading}
            />

            {showForm && (
                <CustomerForm
                    onClose={() => setShowForm(false)}
                    onSuccess={() => {
                        setShowForm(false);
                        fetchCustomers();
                    }}
                    customer={editingCustomer}
                />
            )}

            <CustomerProjectsModal
                isOpen={!!selectedCustomerForProjects}
                onClose={() => setSelectedCustomerForProjects(null)}
                customerId={selectedCustomerForProjects?.id || ''}
                customerName={selectedCustomerForProjects?.displayName || ''}
            />
        </div>
    );
};

export default Customers;
